import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';

const AssistantContext = createContext(null);

const STORAGE_PREFIX = 'jansoftAssistant';

const welcomeMessage = {
  id: 'welcome',
  role: 'assistant',
  content: [
    'Merhaba! Ben **Jansoft Asistan**.',
    'Satış, stok, tahsilat… ya da sadece muhabbet.',
    '',
    'Örnek: “bu yıl satış özeti” · “nasılsın?” · “ne sorabilirim?”',
    'Doğru/yanlış ile eğitmeye devam edebilirsin.'
  ].join('\n'),
  createdAt: new Date().toISOString(),
  feedback: null
};

function withWelcome(items) {
  if (!Array.isArray(items) || items.length === 0) {
    return [{ ...welcomeMessage, id: `welcome-${Date.now()}`, createdAt: new Date().toISOString() }];
  }
  return items;
}

function purgeLocalHistory(userId) {
  try {
    localStorage.removeItem(`${STORAGE_PREFIX}:${userId || 'guest'}`);
    localStorage.removeItem(`${STORAGE_PREFIX}:guest`);
    localStorage.removeItem(`${STORAGE_PREFIX}:null`);
  } catch {
    // ignore
  }
}

export const AssistantProvider = ({ children }) => {
  const { user } = useAuth();
  const userId = user?.id || user?.userId || null;
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([welcomeMessage]);
  const [isSending, setIsSending] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  const loadHistory = useCallback(async () => {
    if (!userId) {
      setMessages([welcomeMessage]);
      return;
    }

    setIsLoadingHistory(true);
    purgeLocalHistory(userId);
    try {
      const response = await api.get('/api/assistant/history', {
        params: { limit: 200 }
      });
      const items = response.data?.data?.items || [];
      setMessages(withWelcome(items));
    } catch {
      setMessages([welcomeMessage]);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [userId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const openAssistant = useCallback(() => setIsOpen(true), []);
  const closeAssistant = useCallback(() => setIsOpen(false), []);
  const toggleAssistant = useCallback(() => setIsOpen((prev) => !prev), []);

  const clearHistory = useCallback(async () => {
    const next = [{
      ...welcomeMessage,
      id: `welcome-${Date.now()}`,
      createdAt: new Date().toISOString()
    }];
    setMessages(next);
    purgeLocalHistory(userId);

    if (!userId) return;
    try {
      await api.post('/api/assistant/history/clear');
      toast.success('Geçmiş temizlendi');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Geçmiş temizlenemedi');
      loadHistory();
    }
  }, [userId, loadHistory]);

  const sendMessage = useCallback(async (rawText) => {
    const text = String(rawText || '').trim();
    if (!text || isSending) return;

    const tempUserId = `u-${Date.now()}`;
    const userMsg = {
      id: tempUserId,
      role: 'user',
      content: text,
      createdAt: new Date().toISOString()
    };

    setMessages((prev) => [...prev, userMsg]);
    setIsSending(true);

    try {
      const response = await api.post('/api/assistant/chat', {
        message: text
      }, { timeout: 120000 });
      const data = response.data?.data || {};
      const reply = data.reply || response.data?.reply || 'Yanıt alınamadı.';

      const serverUser = data.userMessage || null;
      const serverAssistant = data.assistantMessage || null;

      const assistantMsg = serverAssistant || {
        id: `a-${Date.now()}`,
        role: 'assistant',
        content: reply,
        createdAt: new Date().toISOString(),
        intent: data.intent || null,
        periodLabel: data.period?.label || null,
        question: data.question || text,
        chatLogId: data.chatLogId || null,
        qaPairId: data.qaPairId || null,
        feedback: null,
        denied: !!data.denied
      };

      if (!serverAssistant) {
        assistantMsg.intent = data.intent || null;
        assistantMsg.periodLabel = data.period?.label || null;
        assistantMsg.question = data.question || text;
        assistantMsg.chatLogId = data.chatLogId || null;
        assistantMsg.qaPairId = data.qaPairId || null;
        assistantMsg.denied = !!data.denied;
      }

      setMessages((prev) => {
        const withoutTemp = prev.filter((item) => item.id !== tempUserId);
        const nextUser = serverUser || userMsg;
        return [...withoutTemp, nextUser, assistantMsg];
      });
    } catch (error) {
      const assistantMsg = {
        id: `a-err-${Date.now()}`,
        role: 'assistant',
        content: error.response?.data?.message || 'Şu an yanıt veremedim. Biraz sonra tekrar dene.',
        createdAt: new Date().toISOString(),
        isError: true,
        feedback: 'skip'
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } finally {
      setIsSending(false);
    }
  }, [isSending]);

  const submitFeedback = useCallback(async (messageId, isCorrect, userNote = '') => {
    const target = messages.find((item) => item.id === messageId);
    if (!target || target.role !== 'assistant') return;

    const note = String(userNote || '').trim();
    // Yanlış zaten işaretliyse yalnızca yorum ekleme
    const attachingNoteOnly = target.feedback === 'down' && isCorrect === false && !!note;
    if (target.feedback && !attachingNoteOnly) return;

    if (!attachingNoteOnly) {
      setMessages((prev) => prev.map((item) => (
        item.id === messageId ? { ...item, feedback: isCorrect ? 'up' : 'down' } : item
      )));
    }

    try {
      await api.post('/api/assistant/feedback', {
        question: target.question || '',
        reply: target.content,
        intent: target.intent || null,
        periodLabel: target.periodLabel || null,
        chatLogId: target.chatLogId || null,
        conversationDbId: target.dbId || null,
        isCorrect,
        userNote: note || null
      });
      if (attachingNoteOnly) {
        setMessages((prev) => prev.map((item) => (
          item.id === messageId ? { ...item, feedbackNote: note } : item
        )));
        toast.success('Not eklendi');
      } else {
        toast.success(isCorrect ? 'Teşekkürler — doğru olarak kaydedildi' : 'Teşekkürler — yanlış olarak kaydedildi');
      }
    } catch (error) {
      if (!attachingNoteOnly) {
        setMessages((prev) => prev.map((item) => (
          item.id === messageId ? { ...item, feedback: null } : item
        )));
      }
      toast.error(error.response?.data?.message || 'Geri bildirim kaydedilemedi');
    }
  }, [messages]);

  const value = useMemo(() => ({
    isOpen,
    messages,
    isSending,
    isLoadingHistory,
    openAssistant,
    closeAssistant,
    toggleAssistant,
    clearHistory,
    sendMessage,
    submitFeedback
  }), [
    isOpen,
    messages,
    isSending,
    isLoadingHistory,
    openAssistant,
    closeAssistant,
    toggleAssistant,
    clearHistory,
    sendMessage,
    submitFeedback
  ]);

  return (
    <AssistantContext.Provider value={value}>
      {children}
    </AssistantContext.Provider>
  );
};

export const useAssistant = () => {
  const ctx = useContext(AssistantContext);
  if (!ctx) {
    throw new Error('useAssistant must be used within AssistantProvider');
  }
  return ctx;
};

export default AssistantContext;
